import React from 'react';
import { ResponsiveBar } from '@nivo/bar';
import { ArrayHelpers, Copy, Forms, Help, Paging, StringHelpers, SystemText } from 'e4shared';
import { useSelector } from 'react-redux';
import { Slider } from '@material-ui/core';
import { apiPatch } from 'services';

export const BoardMetric = ({ metric, ext }) => {
    const currencySymbol = useSelector(state => state.user.user.currencySymbol);
    const st = (path, ...rest) => SystemText(`Dashboard/Widgets/CompetencyROI/${path}`, ...rest);
    const text = {
        increase: x => st('Increase', x),
        decrease: x => st('Decrease', x),
    };

    return (
        <div className="flex flex-center mb3" key={`metric-${metric.id}`} data-metricid={metric.id}>
            <i className="fa fa-bullseye mr10" />
            <span className="flex-grow ellipse">{metric.name}</span>
            <span>{StringHelpers.formatMetric(metric.format, metric.cur, currencySymbol)}</span>
            <i className="fa fa-long-arrow-right mr10 ml10" />
            <span>{StringHelpers.formatMetric(metric.format, metric.mod, currencySymbol)}</span>
            <Help
                id={`help-metric-${metric.id}-${ext}`}
                target={
                    <label
                        className={`label label-${
                            metric.nDiff > 0 ? 'success' : metric.nDiff < 0 ? 'danger' : 'info'
                        } ml10 mb0`}
                    >
                        {metric.diff >= 0 ? '+' : '-'}
                        <span className="p2">{Math.abs(metric.diff)}</span>%
                    </label>
                }
            >
                {metric.diff >= 0 ? text.increase(metric.fDiff) : text.decrease(metric.fDiff)}
            </Help>
        </div>
    );
};

export const Board = ({ data, item, id, setItem, reload }) => {
    const [percent, setPercent] = React.useState(0);
    const [points, setPoints] = React.useState(0);
    const [mappedData, setMappedData] = React.useState([]);
    const [pagedData, setPagedData] = React.useState([]);
    const [page, setPage] = React.useState(1);
    const [pageSize, setPageSize] = React.useState(6);

    const user = useSelector(state => state.user.user);
    const ratingRange = user.companySettings?.display?.ratingRange || 5;

    const parsed = JSON.parse(item.data);
    const months = parsed.period1?.replace('LM', '');

    const st = (path, ...rest) => SystemText(`Dashboard/Widgets/CompetencyROI/${path}`, ...rest);
    const text = {
        rating: st('Rating'),
        total: st('Count'),
        current: st('Current'),
        model: st('Model'),
        shifting: x => st('Shifting', x),
        points: x => st('Points', x),
        impact: x => st('Impact', x),
        noMetrics: st('NoMetrics'),
        explainer: x => st('Explainer', x),
    };

    const calulateImpact = (data, metric, prop) => {
        let total = 0;
        if (!ArrayHelpers.existsAndHasLength(data)) return 0;
        for (let i = 1; i <= ratingRange; i++) {
            let avg = metric[`rating${i}`] || 0;
            if (!avg) avg = metric[`rating${i - 1}`] || 0;
            if (!avg) avg = metric[`rating${i - 2}`] || 0;
            if (!avg) avg = metric[`rating${i - 3}`] || 0;
            if (!avg) avg = metric[`rating${i - 4}`] || 0;
            const people = data.find(d => d.id === i)[prop];
            total += people * avg;
        }

        return Math.round(total);
    };

    if (data?.data && data?.data.length === 0) {
        return <div>{SystemText('Dashboard/Widgets/Leaderboard/NoResults')}</div>;
    }

    const expandMetric = m => {
        const cur = calulateImpact(mappedData, m, text.current);
        const mod = calulateImpact(mappedData, m, text.model);
        const nDiff = mod - cur;
        const diff = cur === 0 ? 0 : Math.round((mod / cur - 1) * 100);
        const fDiff = StringHelpers.formatMetric(m.format, nDiff);
        return { ...m, cur, mod, nDiff, diff, fDiff };
    };

    React.useEffect(() => {
        const data = JSON.parse(item?.user_Data || '{}');
        setPercent(data?.percent || 0);
        setPoints(data?.points || 0);
    }, [item]);

    React.useEffect(() => {
        if (points > ratingRange - 1) setPoints(ratingRange - 1);
        if (points < ratingRange * -1 + 1) setPoints(ratingRange * -1 + 1);
    }, [points]);

    React.useEffect(() => {
        const p = parseInt(points, 10) || 0;
        const limit = ratingRange - 1;

        if (data?.data) {
            const temp = [...data?.data]
                .sort((a, b) => (a.id > b.id ? 1 : -1))
                .map(d => {
                    if (d.id > ratingRange) return null;
                    let x = { ...d };
                    x[text.current] = d.current;
                    x[text.model] = d.current;
                    return x;
                })
                .filter(d => d);
            for (let i = 0; i < temp.length; i++) {
                const to = i + p < 0 ? 0 : i + p > limit ? limit : i + p;
                const qty = (temp[i][text.current] * percent) / 100;
                temp[to][text.model] += Math.round(qty);
                temp[i][text.model] -= Math.round(qty);
            }

            setMappedData(temp);
        }
    }, [data?.data, percent, points]);

    React.useEffect(() => {
        const values = {
            id: item.id,
            data: JSON.stringify({ percent, points }),
        };

        apiPatch('widget-data', values, user.guid, () => {
            setItem({ ...item, user_Data: values.data });
        });
    }, [percent, points]);

    React.useEffect(() => {
        const metrics = [...data?.metrics]
            .map(m => expandMetric(m))
            .sort((a, b) => (a.diff > b.diff ? -1 : 1))
            .slice(0, 5);

        reload(metrics);
    }, [percent, points, mappedData]);

    React.useEffect(() => {
        const pageSizeGenerator = () => {
            const rowHeight = 23;
            const buttonsHeight = 50;
            const records = data?.metrics?.length;
            const me = document.querySelector(`#${id}`);
            const start = document.querySelector('#paged-start');
            if (!me) return 10;
            const available = me.offsetHeight - start.offsetTop - 10;
            let max = Math.ceil(available / rowHeight);
            const availableWithPaging = available - buttonsHeight;
            let maxWithPaging = Math.ceil(availableWithPaging / rowHeight);
            if (maxWithPaging <= 0) return 1;
            return max > records ? max : maxWithPaging;
        };

        const ps = pageSizeGenerator();

        setPagedData(ArrayHelpers.getPage(data?.metrics, page, ps));
        setPageSize(ps);
    }, [id, item.h, page, data?.metrics]);

    React.useEffect(() => {
        setPage(1);
    }, [item.h]);

    return (
        <>
            <div style={{ height: '265px' }}>
                <ResponsiveBar
                    data={mappedData}
                    keys={[text.current, text.model]}
                    indexBy="id"
                    margin={{ top: 10, right: 130, bottom: 50, left: 60 }}
                    padding={0.2}
                    innerPadding={2}
                    groupMode="grouped"
                    valueScale={{ type: 'linear' }}
                    indexScale={{ type: 'band', round: true }}
                    valueFormat={{ format: ' >-', enabled: false }}
                    colors={{ scheme: 'nivo' }}
                    borderRadius={2}
                    borderWidth={1}
                    borderColor={{ from: 'color', modifiers: [['darker', '0.5']] }}
                    axisTop={null}
                    axisRight={null}
                    axisBottom={{
                        tickSize: 5,
                        tickPadding: 5,
                        tickRotation: 0,
                        legend: text.rating,
                        legendPosition: 'middle',
                        legendOffset: 40,
                    }}
                    axisLeft={{
                        tickPadding: 5,
                        tickRotation: 0,
                        legend: text.total,
                        legendPosition: 'middle',
                        legendOffset: -40,
                    }}
                    enableLabel={false}
                    labelTextColor={{ from: 'color', modifiers: [['darker', 1.6]] }}
                    legends={[
                        {
                            anchor: 'bottom-right',
                            direction: 'column',
                            justify: false,
                            translateX: 120,
                            translateY: 0,
                            itemsSpacing: 2,
                            itemWidth: 100,
                            itemHeight: 20,
                            itemDirection: 'left-to-right',
                            itemOpacity: 0.85,
                            symbolSize: 20,
                            effects: [
                                {
                                    on: 'hover',
                                    style: {
                                        itemOpacity: 1,
                                    },
                                },
                            ],
                        },
                    ]}
                />
            </div>
            <div className="p10" style={{ height: '100px' }}>
                <div className="form-group mbm5">
                    <label>{text.shifting(percent || 0)}</label>
                </div>
                <div className="pointer">
                    <Slider
                        data-testid="slider-roi-percent"
                        value={percent}
                        defaultValue={0}
                        onChange={(e, x) => setPercent(x)}
                        step={5}
                        min={0}
                        max={100}
                        marks
                        aria-label={text.shifting(percent || 0)}
                    />
                </div>
                <Forms.Text
                    data-testid="input-roi-points"
                    label={text.points(points || 0)}
                    type="number"
                    value={points}
                    onChange={x => setPoints(x)}
                />
                <div className="form-group mb0">
                    <label>
                        {text.impact(months)}
                        <Help id="help-impact">
                            <Copy value={text.explainer(months)} />
                        </Help>
                    </label>
                </div>
                {!ArrayHelpers.existsAndHasLength(data?.metrics) && <div>{text.noMetrics}</div>}
                <div id="paged-start" />
                {pagedData.map(m => (
                    <BoardMetric metric={expandMetric(m)} key={`main-metric-${m.id}`} ext="main" />
                ))}
                {data?.metrics?.length > pageSize && (
                    <div className="text-center mt10">
                        <Paging
                            page={page}
                            narrow
                            pageSize={pageSize}
                            recordCount={data?.metrics?.length}
                            goToPage={setPage}
                        />
                    </div>
                )}
            </div>
        </>
    );
};

export default Board;
